import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List } from 'react-window';

const SCROLLBAR_SIZE = 10;

function HeaderRow({
  index,
  style,
  getColumnWidth,
  headerData,
  cellPaddingLeftSize = 3,
  headerTopBorder,
  headerLeftBorder,
  extendLastColumn,
  addWidthToLastColumn,
}) {
  return (
    <div style={{ ...style, width: undefined, display: 'flex' }}>
      {headerData[index].map((col, colIdx) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={colIdx}
          style={{
            minWidth:
              getColumnWidth(colIdx) +
              (headerData[index].length - 1 === colIdx && addWidthToLastColumn
                ? SCROLLBAR_SIZE
                : 0),
          }}
          className={classNames(
            `bo-table-cell fw-bold ps-${cellPaddingLeftSize} pe-1 overflow-hidden border-bottom`,
            headerLeftBorder && colIdx !== 0 && col && !col.isPlaceholder && 'border-start',
            headerTopBorder && 'border-top',
            !extendLastColumn && headerData[index].length - 1 === colIdx && 'border-end',
            col && col.className,
          )}
        >
          {col && !col.isPlaceholder ? col.header : ''}
        </div>
      ))}
    </div>
  );
}

function Row({
  index,
  style,
  processedRows,
  rowsAlly,
  allyIndecies,
  tableColumns,
  getColumnWidth,
  cellPaddingLeftSize = 3,
  rowClickable,
  activeRowIndex,
  setActiveRowIndex,
  hovering,
  manualHovering,
  customHoverClass,
  formatCellConditionally,
  handleRowRightClick,
  addHeightToLastRow,
}) {
  const isAlly = rowsAlly && allyIndecies.includes(index);

  const cellClassName = `bo-table-cell ps-${cellPaddingLeftSize} pe-1 overflow-hidden`;

  const cellStyle = useCallback(
    (colIdx, cell, row, rowIdx) => {
      let newStyle = { width: getColumnWidth(colIdx, isAlly) };

      if (formatCellConditionally) {
        newStyle = {
          ...newStyle,
          ...formatCellConditionally(cell, row, rowIdx, tableColumns[colIdx].key),
        };
      }

      return newStyle;
    },
    [getColumnWidth, formatCellConditionally, tableColumns, isAlly],
  );

  const onMouseAction = useCallback(
    (rowIdx, action) => {
      if (manualHovering) {
        const rows = document.getElementsByClassName(`row-${rowIdx}`);

        if (rows.length > 0) {
          // eslint-disable-next-line no-restricted-syntax
          for (const row of rows) {
            if (action === 'enter') {
              row.classList.add(customHoverClass || 'bo-table-hover-bg');
            } else {
              row.classList.remove(customHoverClass || 'bo-table-hover-bg');
            }
          }
        }
      }

      return undefined;
    },
    [manualHovering, customHoverClass],
  );

  return (
    /* eslint-disable */
      <div
        style={{
          ...style,
          height:
            addHeightToLastRow && index === processedRows.length - 1
              ? style.height + SCROLLBAR_SIZE
              : style.height,
          paddingBottom:
            addHeightToLastRow && index === processedRows.length - 1 ? SCROLLBAR_SIZE : 0,
          width: undefined,
          display: 'flex',
        }}
        className={classNames(
          !isAlly && manualHovering && `row-${index}`,
          !isAlly && hovering && !manualHovering && (customHoverClass || 'bo-table-row-hover'),
          activeRowIndex === index && 'bo-table-active-bg',
          isAlly && 'border-bottom bo-bg-light-gray',
        )}
        onClick={
          rowClickable
            ? () => setActiveRowIndex(prevIndex => (prevIndex === index ? null : index))
            : undefined
        }
        onContextMenu={e => {
          if (handleRowRightClick) {
            handleRowRightClick(e, processedRows[index]);
          }
        }}
        onMouseEnter={() => onMouseAction(index, 'enter')}
        onMouseLeave={() => onMouseAction(index, 'leave')}
      >
        {(isAlly ? rowsAlly.columns : tableColumns).map(({ key, renderValue }, colIdx) => (
          <div
            key={key}
            style={cellStyle(colIdx, processedRows[index][key], processedRows[index], index)}
            className={cellClassName}
          >
            {renderValue
              ? renderValue(processedRows[index][key], processedRows[index])
              : processedRows[index][key]}
          </div>
        ))}
      </div>
      /* eslint-enable */
  );
}

function VirtualizedList({
  height,
  width,
  tableRows,
  rowsAlly,
  frozenColumns = [],
  frozenHeaderConfig,
  tableColumns,
  headerConfig,
  rowKey,
  rowHeight = 36,
  previewColumn,
  getRowHeight,
  headerHeight = 36,
  headerTopBorder,
  headerLeftBorder,
  cellPaddingLeftSize,
  extendColumnKey,
  extendLastColumn = true,
  overscanCount = 10,
  hovering = true,
  customHoverClass,
  formatCellConditionally,
  handleRowRightClick,
  virtualization = true,
}) {
  const { processedRows, allyIndecies } = useMemo(() => {
    if (rowsAlly) {
      const newRows = [];
      const aIndecies = [];

      for (let i = 0; i <= tableRows.length - 1; i += 1) {
        newRows.push(tableRows[i]);

        const key = tableRows[i][rowsAlly.key];
        const item = rowsAlly.data[key];

        if (item) {
          aIndecies.push(newRows.length);
          newRows.push({ ...item, [rowsAlly.key]: `${key}_ally` });
        }
      }

      return { processedRows: newRows, allyIndecies: aIndecies };
    }

    return { processedRows: tableRows, allyIndecies: [] };
  }, [tableRows, rowsAlly]);

  const [activeRowIndex, setActiveRowIndex] = useState(null);

  const manualHovering = useMemo(
    () => hovering && frozenColumns.length > 0,
    [hovering, frozenColumns],
  );

  const outerContainerRef = useRef(null);
  const headerGridRef = useRef(null);
  const mainGridRef = useRef(null);
  const frozenGridRef = useRef(null);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const { current: outerGrid } = outerContainerRef;

    if (outerGrid) {
      const handler = e => {
        e.preventDefault();
        const { deltaX, deltaY } = e;

        const header = headerGridRef.current;
        const frozen = frozenGridRef.current?.element;
        const grid = virtualization ? mainGridRef.current?.element : mainGridRef.current;

        const hiddenHeightContainer = grid?.lastElementChild;
        const hiddenWidthContainer = grid?.firstElementChild;

        if (grid && hiddenHeightContainer && hiddenWidthContainer) {
          let { scrollLeft, scrollTop } = grid;

          if (Math.abs(deltaY) > Math.abs(deltaX)) {
            const maxVScroll = hiddenHeightContainer.clientHeight - grid.clientHeight;

            scrollTop = scrollTop + deltaY < maxVScroll ? scrollTop + deltaY : maxVScroll;
          } else {
            const maxHScroll = hiddenWidthContainer.clientWidth - grid.clientWidth;

            scrollLeft = scrollLeft + deltaX < maxHScroll ? scrollLeft + deltaX : maxHScroll;
          }

          if (header) {
            header.scrollLeft = scrollLeft;
          }

          if (frozen) {
            frozen.scrollTop = scrollTop;
          }

          grid.scrollLeft = scrollLeft;
          grid.scrollTop = scrollTop;
        }
      };

      outerGrid.addEventListener('wheel', handler);

      return () => outerGrid.removeEventListener('wheel', handler);
    }
  }, [virtualization]);

  const onListScroll = useCallback(e => {
    e.preventDefault();

    if (headerGridRef.current) {
      headerGridRef.current.scrollLeft = e.target.scrollLeft;
    }

    if (frozenGridRef.current?.element) {
      frozenGridRef.current.element.scrollTop = e.target.scrollTop;
    }
  }, []);

  const contentHeight = useMemo(
    () =>
      getRowHeight
        ? processedRows.reduce((acc, _, idx) => getRowHeight(idx) + acc, 0)
        : processedRows.length * rowHeight,
    [processedRows, getRowHeight, rowHeight],
  );
  const contentWidth = useMemo(
    () => tableColumns.reduce((acc, value) => acc + value.width, 0),
    [tableColumns],
  );
  const frozenContentWidth = useMemo(
    () => frozenColumns.reduce((acc, value) => acc + value.width, 0),
    [frozenColumns],
  );

  const mainGridWidth = useMemo(() => {
    if (!extendLastColumn && contentWidth + frozenContentWidth + SCROLLBAR_SIZE < width) {
      return contentWidth + SCROLLBAR_SIZE;
    }

    let w = width;

    if (activeRowIndex !== null && !!previewColumn) {
      w -= previewColumn.width;
    }

    if (frozenColumns.length > 0) {
      w -= frozenContentWidth;
    }

    return w;
  }, [
    extendLastColumn,
    width,
    contentWidth,
    activeRowIndex,
    previewColumn,
    frozenColumns,
    frozenContentWidth,
  ]);

  const headerRowCount = headerConfig ? headerConfig.length : 1;

  const isVerticalScrollVisable = contentHeight + headerHeight * headerRowCount > height;
  const isHorizontalScrollVisable = contentWidth + frozenContentWidth > width;

  const getHeaderData = useCallback((cols, config) => {
    if (!config) {
      return [cols.map(col => ({ header: col.header, className: col.className }))];
    }

    const data = [];

    for (let rowIndex = 0; rowIndex < config.length; rowIndex += 1) {
      const rowConfig = config[rowIndex];
      const rowData = [];

      let colIndex = 0;

      rowConfig.forEach(cellConfig => {
        const { header, className, colspan = 1 } = cellConfig;

        rowData[colIndex] = { header, className };

        for (let i = 1; i < colspan; i += 1) {
          colIndex += 1;
          rowData[colIndex] = { header: '', className, isPlaceholder: true };
        }

        colIndex += 1;
      });

      data.push(rowData);
    }

    return data;
  }, []);

  const getColumnWidth = useCallback(
    (index, isAlly) => {
      const columns = isAlly ? rowsAlly.columns : tableColumns;

      if (
        width >
        (activeRowIndex !== null && !!previewColumn
          ? contentWidth + previewColumn.width
          : contentWidth)
      ) {
        if (isAlly) {
          if (extendLastColumn && index === columns.length - 1) {
            return (
              width -
              (activeRowIndex !== null && !!previewColumn ? previewColumn.width : 0) -
              // NOTE: assuming that contentWidth equal to sum of rowsAlly.columns width
              contentWidth -
              (isVerticalScrollVisable ? SCROLLBAR_SIZE : 0) +
              columns[columns.length - 1].width
            );
          }
        } else {
          if (extendColumnKey && index === columns.findIndex(x => x.key === extendColumnKey)) {
            return (
              width -
              (activeRowIndex !== null && !!previewColumn ? previewColumn.width : 0) -
              contentWidth -
              (isVerticalScrollVisable ? SCROLLBAR_SIZE : 0) +
              columns.find(x => x.key === extendColumnKey).width
            );
          }

          if (!extendColumnKey && extendLastColumn && index === columns.length - 1) {
            return (
              width -
              (activeRowIndex !== null && !!previewColumn ? previewColumn.width : 0) -
              contentWidth -
              (isVerticalScrollVisable ? SCROLLBAR_SIZE : 0) +
              columns[columns.length - 1].width
            );
          }
        }
      }

      return columns[index].width;
    },
    [
      width,
      extendLastColumn,
      tableColumns,
      rowsAlly,
      activeRowIndex,
      previewColumn,
      contentWidth,
      isVerticalScrollVisable,
      extendColumnKey,
    ],
  );

  return (
    <div className="d-flex">
      <div ref={outerContainerRef} style={{ width }}>
        <div className="d-flex">
          {frozenColumns.length > 0 && (
            <div
              style={{ height: headerHeight * headerRowCount, width: frozenContentWidth }}
              className="bo-table-header-container"
            >
              {getHeaderData(frozenColumns, frozenHeaderConfig).map((_, rowIdx, headerData) => (
                <HeaderRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={rowIdx}
                  style={{ height: headerHeight }}
                  index={rowIdx}
                  getColumnWidth={index => frozenColumns[index].width}
                  headerData={headerData}
                  cellPaddingLeftSize={cellPaddingLeftSize}
                  headerTopBorder={headerTopBorder}
                  headerLeftBorder={headerLeftBorder}
                  extendLastColumn={extendLastColumn}
                />
              ))}
            </div>
          )}
          <div
            ref={headerGridRef}
            style={{ height: headerHeight * headerRowCount, width: mainGridWidth }}
            className="bo-table-header-container"
          >
            {getHeaderData(tableColumns, headerConfig).map((_, rowIdx, headerData) => (
              <HeaderRow
                // eslint-disable-next-line react/no-array-index-key
                key={rowIdx}
                style={{ height: headerHeight }}
                index={rowIdx}
                getColumnWidth={getColumnWidth}
                headerData={headerData}
                cellPaddingLeftSize={cellPaddingLeftSize}
                headerTopBorder={headerTopBorder}
                headerLeftBorder={headerLeftBorder}
                extendLastColumn={extendLastColumn}
                addWidthToLastColumn={isVerticalScrollVisable}
              />
            ))}
          </div>
        </div>
        <div className="d-flex">
          {frozenColumns.length > 0 && (
            <List
              style={{ height: height - headerHeight * headerRowCount, width: frozenContentWidth }}
              listRef={frozenGridRef}
              className="bo-table-group-container"
              rowComponent={Row}
              rowCount={processedRows.length}
              rowHeight={getRowHeight || rowHeight}
              rowProps={{
                processedRows,
                rowsAlly,
                allyIndecies,
                tableColumns: frozenColumns,
                getColumnWidth: index => frozenColumns[index].width,
                cellPaddingLeftSize,
                rowClickable: !!previewColumn,
                activeRowIndex,
                setActiveRowIndex,
                hovering,
                manualHovering,
                customHoverClass,
                formatCellConditionally,
                handleRowRightClick,
                addHeightToLastRow: isHorizontalScrollVisable && isVerticalScrollVisable,
              }}
            />
          )}
          {virtualization ? (
            <List
              style={{ height: height - headerHeight * headerRowCount, width: mainGridWidth }}
              listRef={mainGridRef}
              className="bo-table-container"
              rowComponent={Row}
              rowCount={processedRows.length}
              rowHeight={getRowHeight || rowHeight}
              rowProps={{
                processedRows,
                rowsAlly,
                allyIndecies,
                tableColumns,
                getColumnWidth,
                cellPaddingLeftSize,
                rowClickable: !!previewColumn,
                activeRowIndex,
                setActiveRowIndex,
                hovering,
                manualHovering,
                customHoverClass,
                formatCellConditionally,
                handleRowRightClick,
              }}
              onScroll={onListScroll}
              overscanCount={overscanCount}
            />
          ) : (
            <div
              ref={mainGridRef}
              style={{ height: height - headerHeight * headerRowCount, width: mainGridWidth }}
              className="bo-table-container overflow-auto"
              onScroll={onListScroll}
            >
              <div className="d-table">
                {processedRows.map((row, rowIdx) => (
                  <Row
                    key={rowKey ? row[rowKey] : rowIdx}
                    style={{ height: getRowHeight ? getRowHeight(rowIdx) : rowHeight }}
                    index={rowIdx}
                    processedRows={processedRows}
                    rowsAlly={rowsAlly}
                    allyIndecies={allyIndecies}
                    tableColumns={tableColumns}
                    getColumnWidth={getColumnWidth}
                    cellPaddingLeftSize={cellPaddingLeftSize}
                    rowClickable={!!previewColumn}
                    activeRowIndex={activeRowIndex}
                    setActiveRowIndex={setActiveRowIndex}
                    hovering={hovering}
                    manualHovering={manualHovering}
                    customHoverClass={customHoverClass}
                    formatCellConditionally={formatCellConditionally}
                    handleRowRightClick={handleRowRightClick}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {activeRowIndex !== null && !!previewColumn && (
        <div style={{ width: previewColumn.width, height }}>
          <div
            style={{ width: previewColumn.width, height: headerHeight }}
            className={`bo-table-cell fw-bold px-1 overflow-hidden border-bottom border-start ${
              headerTopBorder ? 'border-top' : ''
            }`}
          >
            {previewColumn.header}
          </div>
          <div
            style={{ width: previewColumn.width, height: height - headerHeight * headerRowCount }}
            // eslint-disable-next-line max-len
            className="bo-table-cell bo-table-container align-items-start overflow-auto border-start"
          >
            {previewColumn.renderValue
              ? previewColumn.renderValue(
                  processedRows[activeRowIndex][previewColumn.key],
                  processedRows[activeRowIndex],
                )
              : processedRows[activeRowIndex][previewColumn.key]}
          </div>
        </div>
      )}
    </div>
  );
}

export default VirtualizedList;
